import React from "react";
import { useIntl } from "react-intl";
import { Box, Button, DialogActions, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { CUSTOMER_CARD_STEPS } from "../constants/steps";
import { dialogButtonBaseSx, ui } from "../styles";
import { CustomerLogo } from "./primitives";

export const DialogHeader = ({ companyName, existingCard, logoSrc, masterOrganizationName, onClose }) => {
  const intl = useIntl();
  const title = intl.formatMessage({ id:existingCard ? "admin.crm.customerCard.edit" : "admin.crm.customerCard.create" });
  return (
    <DialogTitle sx={{ px:{ xs:1.5, sm:2 }, py:1.25, bgcolor:"#fff", borderBottom:`1px solid ${ui.border}`, flexShrink:0 }}>
      <Stack direction="row" alignItems="center" spacing={1.05}>
        <CustomerLogo src={logoSrc} size={36} fallbackIconSize={19}/>
        <Box sx={{ minWidth:0, flex:1 }}>
          <Typography sx={{ color:ui.text, fontSize:18, lineHeight:1.2, fontWeight:600, letterSpacing:"-.012em" }}>{title}</Typography>
          <Typography noWrap sx={{ mt:.18, color:ui.muted, fontSize:10.7 }}>{masterOrganizationName || companyName || intl.formatMessage({ id:"admin.crm.customerCard.standalone" })}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label={intl.formatMessage({ id:"admin.crm.customerCard.actions.close" })} sx={{ width:32, height:32, color:ui.muted, borderRadius:1, "&:hover":{ bgcolor:ui.soft, color:ui.text } }}>
          <CloseRoundedIcon sx={{ fontSize:18 }}/>
        </IconButton>
      </Stack>
    </DialogTitle>
  );
};

export const PremiumStepper = ({ activeStep }) => {
  const intl = useIntl();
  return <Box sx={{ position:"relative", display:"grid", gridTemplateColumns:`repeat(${CUSTOMER_CARD_STEPS.length}, minmax(0, 1fr))`, px:{ xs:0, sm:.4 } }}>
    <Box sx={{ position:"absolute", top:18, left:{ xs:"12.5%", sm:"12.8%" }, right:{ xs:"12.5%", sm:"12.8%" }, height:"1px", bgcolor:ui.borderStrong }}/>
    <Box sx={{ position:"absolute", top:18, left:{ xs:"12.5%", sm:"12.8%" }, width:`${activeStep / (CUSTOMER_CARD_STEPS.length - 1) * 75}%`, height:"1px", bgcolor:"primary.main", transition:"width 180ms ease" }}/>
    {CUSTOMER_CARD_STEPS.map((step,index) => {
      const Icon = step.icon, active = index === activeStep, completed = index < activeStep, reached = active || completed;
      return (
        <Stack key={step.key} alignItems="center" spacing={.5} sx={{ position:"relative", zIndex:1 }}>
          <Box sx={{ mt:"2px", width:32, height:32, borderRadius:"50%", display:"grid", placeItems:"center", bgcolor:reached ? "primary.main" : "#fff",
            color:reached ? "#fff" : ui.subtle, border:"1px solid", borderColor:reached ? "primary.main" : ui.borderStrong,
            boxShadow:active ? theme => `0 0 0 4px ${alpha(theme.palette.primary.main,.09)}` : "none",
            transition:"background-color 160ms ease, color 160ms ease, box-shadow 160ms ease" }}>
            {completed ? <CheckCircleOutlineRoundedIcon sx={{ fontSize:16 }}/> : <Icon sx={{ fontSize:16 }}/>} 
          </Box>
          <Typography sx={{ color:active ? ui.text : completed ? ui.muted : ui.subtle, fontSize:10, lineHeight:1.2, fontWeight:active ? 600 : 500 }}>{intl.formatMessage({ id:step.messageId })}</Typography>
        </Stack>
      );
    })}
  </Box>;
};

export const DialogFooter = ({
  activeStep,
  canSave,
  existingCard,
  isLastStep,
  onClose,
  onSave,
  setActiveStep,
}) => {
  const intl = useIntl();

  return (
    <DialogActions
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.05,
        bgcolor: "#fff",
        borderTop: `1px solid ${ui.border}`,
        justifyContent: "space-between",
        flexShrink: 0,
      }}
    >
      <Box>
        {activeStep > 0 && (
          <Button
            size="small"
            startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setActiveStep((step) => step - 1)}
            sx={{
              ...dialogButtonBaseSx,
              px: 0.75,
              color: ui.muted,
              fontWeight: 520,
              "&:hover": { bgcolor: ui.soft, color: ui.text },
            }}
          >
            {intl.formatMessage({ id: "admin.crm.customerCard.actions.previous" })}
          </Button>
        )}
      </Box>

      <Stack direction="row" spacing={0.65}>
        <Button
          size="small"
          variant="outlined"
          onClick={onClose}
          sx={{
            ...dialogButtonBaseSx,
            px: 1.25,
            borderColor: ui.borderStrong,
            color: ui.text,
            fontWeight: 550,
            "&:hover": { borderColor: "#c5ccd6", bgcolor: ui.soft },
          }}
        >
          {intl.formatMessage({ id: "admin.crm.customerCard.actions.cancel" })}
        </Button>

        {!isLastStep ? (
          <Button
            size="small"
            variant="contained"
            endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setActiveStep((step) => step + 1)}
            sx={{
              ...dialogButtonBaseSx,
              px: 1.3,
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: "0 2px 6px rgba(16,24,40,.12)" },
            }}
          >
            {intl.formatMessage({ id: "admin.crm.customerCard.actions.next" })}
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            disabled={!canSave}
            onClick={onSave}
            sx={{
              ...dialogButtonBaseSx,
              px: 1.35,
              fontWeight: 600,
              boxShadow: "none",
            }}
          >
            {intl.formatMessage({
              id: existingCard
                ? "admin.crm.customerCard.actions.saveChanges"
                : "admin.crm.customerCard.actions.create",
            })}
          </Button>
        )}
      </Stack>
    </DialogActions>
  );
};